import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight } from "@/components/Insight";
import {
  Tip, KeyIdea, Pull, Steps, Step, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation33() {
  const { lesson, course, accent } = findLesson("piano-foundation", "33");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>Part 2 flips the rule: <strong>all five fingers stay pressed down</strong>, lift only one at a time. Even harder than holding a single finger.</Highlight>
        <Highlight>Four exercises layered on top: <strong>linear</strong> (1-2-3-4-5), <strong>alternates</strong> (C-E-D-F-E-G), <strong>triplet groupings</strong> (C-D-E, D-E-F, E-F-G), and <strong>two-note pairs</strong> (C+E, D+F, E+G).</Highlight>
        <Highlight>Take any drill into <strong>minor</strong> by flattening the third — E becomes E♭. Same fingering, darker mood.</Highlight>
        <Highlight>Switch to <strong>spread fingering</strong> when ready: stretch the five-finger position to C-E-G-A-C and repeat the lifts. The hand learns wider stretches.</Highlight>
        <Highlight>You can do all of this on a <strong>flat surface</strong> — your leg, a book, a desk. we call them <em>"table exercises"</em>; he used them in college to prepare for jams.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="The flip" title="All down · lift only one" accent={accent.hex}>
        <Pull>
          In part 1 what we ended up doing was we kept one finger down and we moved all
          the other fingers around that one held down finger. In this particular part we
          are going to keep all our fingers down.
        </Pull>

        <p>
          Place all five fingers on the C-major five-finger position (C, D, E, F, G) and
          press all five down at once. Now play the next note in whatever pattern you choose
          by lifting <em>only that one finger</em> while the other four stay glued to their
          keys. The four "held" fingers create a small chord cluster underneath; the lifted
          finger is the one making the melody.
        </p>

        <KeyIdea>
          The other fingers want to lift along with the active one — that's their natural
          coupling. The whole point of the drill is to break that coupling. With practice,
          your fingers stop sympathising with each other and start acting independently.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="Drill 1 · Linear" title="C - D - E - F - G - F - E - D - C" accent={accent.hex}>
        <p>
          Same five-note up-and-down as the original five-finger drill from Lesson 5 — but
          now with the <em>other four fingers staying pressed down</em>. The held cluster
          rings while you walk the scale. Slow it down to half speed at first.
        </p>

        <Tip>
          You'll feel which fingers are "lazy". Whichever ones tend to lift along with the
          active finger are the ones to drill the longest. Often the ring finger is the
          worst offender — it doesn't like to stay still while the middle or pinky moves.
        </Tip>
      </Insight>

      <Insight eyebrow="Drill 2 · Alternates" title="C - E - D - F - E - G — and back" accent={accent.hex}>
        <p>
          Skip every other note instead of going consecutively. The pattern:
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          Up:   C - E - D - F - E - G<br />
          Down: G - E - F - D - E - C
        </p>

        <p>
          Same all-fingers-down rule. This drill is significantly trickier than the linear
          one because the active finger keeps jumping over still-pressed fingers in
          between. The lift-and-drop needs to be cleanly isolated to one knuckle.
        </p>
      </Insight>

      <Insight eyebrow="Drill 3 · Triplet groupings" title="C-D-E · D-E-F · E-F-G" accent={accent.hex}>
        <p>
          Three-note overlapping groups, walking up and down. With the all-fingers-down
          rule, this builds a triplet feel naturally.
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          C-D-E · D-E-F · E-F-G · F-E-D · E-D-C
        </p>

        <Tip>
          Accent the first note of every triplet group — louder on the "1", lighter on the
          "2-3". The accent gives the drill a clear musical pulse and strengthens the
          accenting finger as a side effect.
        </Tip>

        <p>
          Slam these notes together and they add a resonance — sympathetic resonance — with
          respect to each other. It sounds really cool when you hear it.
        </p>
      </Insight>

      <Insight eyebrow="Drill 4 · Two-note pairs" title="C+E · D+F · E+G — together" accent={accent.hex}>
        <p>
          The fourth drill is the hardest of the four: lift <strong>two fingers at once</strong>{" "}
          while the other three stay pressed. The pattern:
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          C + E together · D + F together · E + G together · D + F · C + E
        </p>

        <p>
          Try the minor version too — make E into E♭. So C+E♭, D+F, E♭+G. Same fingering,
          minor flavour.
        </p>
      </Insight>

      <Insight eyebrow="Stretch the position" title="Spread the five fingers · same drills" accent={accent.hex}>
        <p>
          Once the close five-finger position feels solid, widen the hand. Move from C-D-E-F-G
          to <strong>C-E-G-A-C</strong> — the same five fingers, but now spanning an octave
          instead of a fifth. Repeat all four drills with the new spread. Your fingers learn
          to stay still across a larger distance.
        </p>
      </Insight>

      <Insight eyebrow="Practise anywhere" title="The 'table exercises'" accent={accent.hex}>
        <Pull>
          You can practise this exercise pretty much wherever you are. I used to do it
          in my college days and I feel this really helped. I used to call them table
          exercises and it really helped. It was preparing me for a jam.
        </Pull>

        <p>
          The lift-and-press is the same physical event whether or not a key is under the
          finger. The held-cluster pressure is the same. The only thing missing on a table
          is the sound — but the muscle training transfers exactly. Use this when you don't
          have a piano: at a desk, on a bus, between meetings.
        </p>

        <Steps>
          <Step>
            All five fingers pressed flat on a surface. Lift one at a time — thumb, index,
            middle, ring, pinky.
          </Step>
          <Step>
            Lift two at a time — thumb-and-middle, index-and-ring, middle-and-pinky.
          </Step>
          <Step>
            Walk the linear / alternates / triplet patterns from above, all on the surface.
          </Step>
          <Step>
            Switch hands. Each one gets the same workout.
          </Step>
        </Steps>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-34"
        label="Lesson 34 · Practice Scales with a Song"
        why="Independence drills are technical. The next short lesson uses a real song to bring scales, rhythm and finger control together — practice as music, not just as exercise."
      />
    </LessonShell>
  );
}
